import { TranslationsFunc } from '@lang';
import { z } from 'zod';

const guestSchema = z.object({
  title: z.string().optional().nullable(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  gender: z.string().optional(),
  room: z.number().optional(),
  externalSpaId: z.union([z.string(), z.number()]).optional().nullable(),
  id: z.union([z.string(), z.number()]).optional(),
  selectedGuestId: z.string().optional(),
  isLead: z.boolean().optional(),
  loggedInUser: z
    .object({ id: z.union([z.string(), z.number()]) })
    .optional()
    .nullable(),
});

const extraItemSchema = z
  .object({
    isSelected: z.boolean().optional(),
    quantity: z.number().optional(),
    option: z.number().optional(),
  })
  .optional();

export const buildRoomRateFormSchema = (_t: TranslationsFunc) =>
  z
    .object({
      isMultiRoom: z.boolean(),
      guests: z.array(guestSchema),
      note: z.string().optional(),
      extras: z
        .union([z.array(extraItemSchema), z.record(z.string(), z.array(extraItemSchema))])
        .optional(),
    })
    .superRefine((data, ctx) => {
      if (!data.guests || data.guests.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'required',
          path: ['guests'],
        });
        return;
      }

      if (data.isMultiRoom) {
        const roomsSeen: Record<number, boolean> = {};
        data.guests.forEach((guest, index) => {
          const roomNum = guest.room ?? 1;
          if (!roomsSeen[roomNum]) {
            roomsSeen[roomNum] = true;
            if (!guest.title?.trim()) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'required',
                path: ['guests', index, 'title'],
              });
            }
            if (!guest.firstName?.trim()) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'required',
                path: ['guests', index, 'firstName'],
              });
            }
            if (!guest.lastName?.trim()) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'required',
                path: ['guests', index, 'lastName'],
              });
            }
          }
        });
      } else {
        data.guests.forEach((guest, index) => {
          const isLead = index === 0;
          const hasAnyName =
            !!guest.title?.trim() || !!guest.firstName?.trim() || !!guest.lastName?.trim();
          if (!isLead && !hasAnyName) {
            return;
          }
          if (!guest.title?.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'required',
              path: ['guests', index, 'title'],
            });
          }
          if (!guest.firstName?.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'required',
              path: ['guests', index, 'firstName'],
            });
          }
          if (!guest.lastName?.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'required',
              path: ['guests', index, 'lastName'],
            });
          }
        });
      }
    });

const roomRateFormSchemaForType = buildRoomRateFormSchema(
  ((key: string) => key) as unknown as TranslationsFunc
);

export type RoomRateFormValues = z.infer<typeof roomRateFormSchemaForType>;
